/**
 * AB#21837 — the WhatsApp thread of one order: whatever was said about it in
 * the WhatsApp groups (factories, shipping agents, suppliers, sometimes a
 * customer or an internal group), read from the same feed and through the
 * same rules as ELSA will (AB#21629). The server decides what may be shown —
 * evidenced links only, no names of people — this only fetches and shapes it.
 */
import { computed, ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';

export interface WhatsAppMessage {
  at: string;
  chat?: string;
  /** Which side wrote it: us, or whoever the group is. */
  by: 'them' | 'custimoo';
  text?: string;
  attachment?: string;
  attachment_status?: string;
  is_reply?: boolean;
  linked_by?: string;
  /** AB#21841 — who wrote it (WhatsApp profile name), and their platform identity when staff. */
  sender?: string;
  sender_org?: string;
  sender_user?: { id: number; avatar_url?: string | null };
  /** Storage key of the attachment; exchanged for a short link on demand. */
  file_key?: string;
  id?: string;
  /** Linked by timing only — shown greyed when "surrounding messages" is on. */
  context?: boolean;
}

export interface PackingFact {
  sent_at?: string;
  supplier_order_no?: string;
  doc_no?: string;
  customer?: string;
  carton_qty?: number;
  styles?: string[];
  document?: string;
  file_key?: string;
}

export interface WhatsAppThreadPayload {
  available: boolean;
  messages: WhatsAppMessage[];
  packing: PackingFact[];
  earlier_messages_omitted: boolean;
  order_no?: string;
  min_link_confidence?: number;
}

/** How a message was tied to this order, in words a person can weigh. */
export const LINKED_BY: Record<string, string> = {
  pdf: 'from a packing list',
  text: 'order number in the message',
  mapped: 'supplier order number',
  reply: 'replying to a linked message',
};

export const dayLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
};

export const timeLabel = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/** WhatsApp mentions arrive as @<user id>; they are not phone numbers. */
export const renderMentions = (text: string): string => text.replace(/@\d{9,}/g, '@someone');

/** What the sender line should say. */
export const senderLabel = (m: WhatsAppMessage): string => {
  if (m.sender) return m.sender;
  return m.by === 'custimoo' ? 'Custimoo' : m.chat || 'Contact';
};

const isImage = (name: string): boolean => /\.(jpe?g|png|gif|webp)$/i.test(name);
const isPdf = (name: string): boolean => /\.pdf$/i.test(name);
export const attachmentKind = (name?: string): 'image' | 'pdf' | 'file' =>
  !name ? 'file' : isImage(name) ? 'image' : isPdf(name) ? 'pdf' : 'file';

/** A short pre-signed link for one of the order's attachments (15 min). */
export const fileLink = async (
  orderItemId: number,
  key: string,
  download = false
): Promise<string | null> => {
  try {
    const res = await http.get(`order-items/${orderItemId}/whatsapp/file`, {
      params: { key, ...(download ? { download: 1 } : {}) },
    });
    return res.data?.success === true ? (res.data.result?.url as string) : null;
  } catch {
    return null;
  }
};

export type WhatsAppThread = ReturnType<typeof useWhatsAppThread>;

export const useWhatsAppThread = (orderItemId: number) => {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const data = ref<WhatsAppThreadPayload | null>(null);
  const withContext = ref(false);

  const load = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;
    try {
      const res = await http.get(`order-items/${orderItemId}/whatsapp`, {
        params: withContext.value ? { context: 1 } : {},
      });
      if (res.data?.success === true) {
        data.value = res.data.result as WhatsAppThreadPayload;
      } else {
        error.value = res.data?.message || 'The WhatsApp messages could not be loaded.';
      }
    } catch {
      // A tab that failed to load is not a page-level error; no toast.
      error.value = 'The WhatsApp messages could not be loaded.';
    } finally {
      isLoading.value = false;
    }
  };

  /** Messages grouped by day, oldest first, for the date rules in the list. */
  const days = computed(() => {
    const out: { label: string; messages: WhatsAppMessage[] }[] = [];
    for (const m of data.value?.messages ?? []) {
      const label = dayLabel(m.at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.messages.push(m);
      else out.push({ label, messages: [m] });
    }
    return out;
  });

  const toggleContext = async (): Promise<void> => {
    withContext.value = !withContext.value;
    await load();
  };

  return { isLoading, error, data, days, load, withContext, toggleContext };
};
